import React, { useEffect } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import Body from '../Components/layout/Body';
import { FIRST_METHOD_CATEGORY } from './ConstForTestDriveSection';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const TD_CHOOSE_FIRST_HELP = `
  <div>
    <p><strong>Purpose: </strong>Here we are just checking to see if you might be considering a standard input method as a possibility for this client.  
    A “standard input method” would be something that comes with the client’s device, like the built-in keyboard for a laptop, or the touchscreen for a tablet. 
    The answer to this question doesn’t change how Access Navigator behaves. 
    It’s just a question for you to consider as you start to sift through possible solutions for this client. 
    </p>
    <p>
    Why would you Test-drive a standard method? Using a standard input method has some advantages, in that it is simpler, always available, and has no additional cost. 
    Some clients prefer using the same thing that “everyone else” uses.  
    And some clients are already trying to “muddle through” with standard input methods, even though they have a motor impairment.  
    In cases like these, it can be worth Test-driving a standard input method, as a baseline.     </p>
    <p>
    Note that the behavior of standard input methods can vary depending on the operating system settings. 
    <strong>For example</strong>, someone who has trouble controlling the pointer with the default trackpad settings might do much better with a slower tracking speed.  
    You could do a quick Test-drive with the default trackpad settings, to see the issues and get a sense of their importance.  
    Then you could do a second Test-drive with a slower tracking speed, and see how much that improves things.  
    If adjusting settings still doesn’t yield good performance, you could then try out an alternative pointing device.     </p>
  </div>
`;
const TD_CHOOSE_FIRST_ACTION_HELP = `
  <div>
    <p><strong>Purpose: </strong>You've got an active Test-drive that has at least one activity in it. And now we are back at the Choose It tab, at the "top" of the Test-drive section.
    You might have gotten here because:</p>
    <ol>
    <li>You want to start a <strong>brand new</strong> Test-drive -- maybe you clicked Choose It before visiting every screen for the current Test-drive.</li>
    <li>Or maybe you just want to view or update something about the <strong>current</strong> Test-drive.</li>
    </ol>
    <p>Because we're not sure what you want to do, we're asking for clarification.</p>
    <p><strong>What to do: </strong>Not sure what to choose? Generally, starting a new Test-drive means that you want to change something about the access method (maybe the access method itself, or something about its setup), 
    and then try some activities with it to see how well it works for the client. So if that describes you, go ahead and answer Yes.</p>
    <p><strong>Note: </strong>No matter what you choose, your data to this point have all been saved, so no worries about that.</p>
    
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),  
  isLowTechPath: PropTypes.bool,
  hasCurrentMethod: PropTypes.string,
  firstMethodCategory: PropTypes.string,
  onChangeFirstMethodCategory: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  isLowTechPath: false,
  hasCurrentMethod: 'false',
  firstMethodCategory: FIRST_METHOD_CATEGORY.ANY
};

//content sub-component with unique content for this page
function TestDriveChooseFirstContent(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const isLowTechPath = props.isLowTechPath;
  const hasCurrentMethod = props.hasCurrentMethod;
  const firstMethodCategory = props.firstMethodCategory;
  const onChangeFirstMethodCategory = props.onChangeFirstMethodCategory;

  const currMethodBtns = [
    {
      value: FIRST_METHOD_CATEGORY.CURRENT,
      label: `Yes, let's get a baseline with the current access method.`,
      moreInfo: ''
    },
    {
      value: FIRST_METHOD_CATEGORY.ANY,
      label: `No, we'll try a different access method.`,
      moreInfo: ''
    }
  ];

  const stdMethodBtns = [
    {
      value: FIRST_METHOD_CATEGORY.STANDARD,
      label: `Yes, let's get a baseline with a regular input method.`,
      moreInfo: ''
    },
    {
      value: FIRST_METHOD_CATEGORY.ANY,
      label: `No, we'll start with some form of alternative access.`,
      moreInfo: ''
    }
  ];

  if(hasCurrentMethod==='true'){
    return(
      <>
      <p>You mentioned that {firstName} has a current access method.
      We suggest doing a Test-drive with that, to get some baseline information.</p>
      <Form>
        <FormGroup tag="fieldset">
          <legend>Do you want to do a test-drive with {firstName}'s current access method?</legend>
          {currMethodBtns.map((btn, index) =>
            <FormGroup check key={index}>
              <Input
                id={btn.value}
                value={btn.value}
                checked={firstMethodCategory===btn.value}
                onChange={onChangeFirstMethodCategory}
                name="first-method-category"
                type="radio"
              />
              {' '}
              <Label for={btn.value} check>
                {btn.label}
                {' '}
              </Label>
            </FormGroup>
          )}
        </FormGroup>
      </Form>
      </>
    );
  }
  else if(!isLowTechPath){
    return(
      <>
      <p>If you think {firstName} may be able to use standard input methods (like a regular keyboard or trackpad),
      we recommend exploring that to get some baseline information.</p>
      <Form>
        <FormGroup tag="fieldset">
          <legend>Do you want to do a test-drive with a standard input method?</legend>
          {stdMethodBtns.map((btn, index) =>
            <FormGroup check key={index}>
              <Input
                id={btn.value}
                value={btn.value}
                checked={firstMethodCategory===btn.value}
                onChange={onChangeFirstMethodCategory}
                name="first-method-category"
                type="radio"
              />
              {' '}
              <Label for={btn.value} check>
                {btn.label}
                {' '}
              </Label>
            </FormGroup>
          )}
        </FormGroup>
      </Form>
      </>
    );
  }
  else{
    return(
      <>
      <p>As you'll see on the next page, methods for the low-tech case tend to fall into two types:</p>
      <ol>
        <li>Partner-observed action: where the client does some sort of action that can been noticed by a partner who is nearby. This might be an eye movement, or a facial muscle twitch, or anything that works well for {firstName}.</li>
        <li>Switch access: where the client does an action that triggers a switch hit. </li>
      </ol>
      <p>For either type, you want to find an action that {firstName} can do reliably and comfortably. If you've gone through the earlier sections of Access Navigator, you probably have some ideas about what actions might work well for {firstName}.</p>
      <p>Check out the Learn More links in this section for more ideas.</p>
      </>
    )
  }
}

function TestDriveChooseFirstActionContent(props) {
  const startNewTestDrive = props.startNewTestDrive;
  const onChangeStartNewTestDrive = props.onChangeStartNewTestDrive;
  console.log("TDCFAC, startNewTestDrive: " + startNewTestDrive);
  const startNewTestDriveBtns = [
    {
      value: 'true',
      label: `Yes, let's start a new test-drive`,
      moreInfo: ''
    },
    {
      value: 'false',
      label: `No, let's stay in the current test-drive`,
      moreInfo: ''
    }
  ];
  return(
    <>
    <p><span className="aa-notice-me fa fa-exclamation-triangle">&nbsp;</span>It looks like there is already a current Test-drive active (and saved).</p>
    <p>We're not exactly sure what you want to do now, so please clarify.</p>
    <Form>
      <FormGroup tag="fieldset">
        <legend>Do you want to start a *new* Test-drive?</legend>
        {startNewTestDriveBtns.map((btn, index) =>
          <FormGroup check key={index}>
            <Input
              id={btn.value}
              value={btn.value}
              checked={startNewTestDrive===btn.value}
              onChange={onChangeStartNewTestDrive}
              name="start-new-test-drive"
              type="radio"
            />
            {' '}
            <Label for={btn.value} check>
              {btn.label}
              {' '}
            </Label>
          </FormGroup>
        )}
      </FormGroup>
    </Form>
    <p><strong>Either way, your current test-drive has been saved.</strong></p>
    </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function TestDriveChooseFirst(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const isActiveTestDrive = props.isActiveTestDrive;
  const isLowTechPath = props.isLowTechPath;
  const hasCurrentMethod = props.hasCurrentMethod;
  const startNewTestDrive = props.startNewTestDrive;
  const onChangeStartNewTestDrive = props.onChangeStartNewTestDrive;
  const firstMethodCategory = props.firstMethodCategory;
  const onChangeFirstMethodCategory = props.onChangeFirstMethodCategory;
  const saveFormData = props.saveFormData;
  const onContinue = props.onContinue;
  const dev = props.dev || {};

  let title = isActiveTestDrive ? 'Please clarify what you want to do now' : `Let's choose the access method for ${firstName}'s Test-drive.`;
  let learnMore = isActiveTestDrive
    ? {linkText: "Learn More about what we're asking you here",
      title: "Test-drive: Clarify new vs existing",
      content: TD_CHOOSE_FIRST_ACTION_HELP
      }
    :
      {linkText: "Learn More about considering an access method for the Test-drive",
      title: "Considerations for the Test-drive",
      content: TD_CHOOSE_FIRST_HELP
      };
  const pageData = {
    image: "./assets/images/target-icon.jpg",
    faIcon: "fa-car aa-fa-3x",
    pageSymbolClassName: "aa-section-symbol test-drive",

    title: title,
    subTitle: '',
    learnMore: learnMore,
    nextRoute: "",
    sidebar: false
  }

  console.log("Rendering TestDriveChooseFirst, isActiveTestDrive is: " + isActiveTestDrive);
  
  useEffect(() => {
    console.log('TDCFirst mounted');
    return () => {
      console.log('TDCFirst unmounted and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        isActiveTestDrive ?
        <TestDriveChooseFirstActionContent 
          firstName={firstName}
          pronouns={pronouns}
          startNewTestDrive={startNewTestDrive}
          onChangeStartNewTestDrive={onChangeStartNewTestDrive}
        />
        :
        <TestDriveChooseFirstContent 
          firstName={firstName}
          pronouns={pronouns}
          isLowTechPath={isLowTechPath}
          hasCurrentMethod={hasCurrentMethod}
          firstMethodCategory={firstMethodCategory}
          onChangeFirstMethodCategory={onChangeFirstMethodCategory}
        />
      }
      sidebarComponent = {
        dev.showDevSidebar && 
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
       />
      }
      onContinue={onContinue}  
    />
    </>
  );
}

TestDriveChooseFirst.propTypes = propTypes;
TestDriveChooseFirst.defaultProps = defaultProps;

export default TestDriveChooseFirst;